import React, { useEffect, useMemo, useState } from 'react';
import {
  App,
  Breadcrumb,
  Button,
  Empty,
  Input,
  Modal,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, PlusOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { Product, ProductVariant } from '../../types/pz';
import {
  buildProductSelectorSearchText,
  getProductSelectionLabel,
  getProductSelectorDimensions,
  getProductSelectorEan,
  getProductVariantAttributeEntries,
  getProductVariantDisplayName,
  getProductVariantName,
  isRealProductVariant,
} from '../../utils/productSelectorLabel';
import { formatUnit } from '../../utils/units';
import './ProductPickerModal.css';

const { Text } = Typography;
const UNCATEGORIZED_KEY = '__uncategorized__';
const PREFERRED_KEY = '__preferred__';

interface CatalogProduct {
  id: string;
  product: Product;
  defaultVariant?: ProductVariant;
  realVariants: ProductVariant[];
  selectableVariants: ProductVariant[];
}

interface ProductPickerModalProps {
  open: boolean;
  variants: ProductVariant[];
  loading?: boolean;
  selectedVariantId?: string | null;
  showInventory?: boolean;
  filterAvailable?: boolean;
  preferredVariantIds?: readonly string[];
  canCreate?: boolean;
  onRequestCreate?: () => void;
  onSelect: (variant: ProductVariant) => void;
  onCancel: () => void;
}

const getCategoryKey = (product?: Product) => product?.productCategoryId || UNCATEGORIZED_KEY;

const hasAvailableInventory = (variant: ProductVariant) => (
  Number(variant.inventory?.quantityAvailable || 0) > 0
);

const ProductPickerModal: React.FC<ProductPickerModalProps> = ({
  open,
  variants,
  loading = false,
  selectedVariantId,
  showInventory = false,
  filterAvailable = false,
  preferredVariantIds = [],
  canCreate = false,
  onRequestCreate,
  onSelect,
  onCancel,
}) => {
  const { t } = useTranslation(['products', 'common']);
  const { message } = App.useApp();
  const [search, setSearch] = useState('');
  const [selectedCategoryKey, setSelectedCategoryKey] = useState<string>();
  const [selectedProductId, setSelectedProductId] = useState<string>();

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSelectedCategoryKey(undefined);
    setSelectedProductId(undefined);
  }, [open]);

  const products = useMemo<CatalogProduct[]>(() => {
    const grouped = new Map<string, ProductVariant[]>();
    variants.forEach((variant) => {
      if (!variant.product?.id) return;
      const rows = grouped.get(variant.product.id) || [];
      rows.push(variant);
      grouped.set(variant.product.id, rows);
    });

    return Array.from(grouped.entries()).map(([id, productVariants]) => {
      const defaultVariant = productVariants.find((variant) => variant.isDefault)
        || productVariants.find((variant) => variant.variantName === 'Standard');
      const realVariants = productVariants.filter(isRealProductVariant);
      const candidates = [
        ...(defaultVariant ? [defaultVariant] : []),
        ...realVariants,
      ];
      const selectableVariants = filterAvailable
        ? candidates.filter(hasAvailableInventory)
        : candidates;
      return {
        id,
        product: productVariants[0].product as Product,
        defaultVariant,
        realVariants,
        selectableVariants,
      };
    }).filter((product) => product.selectableVariants.length > 0)
      .sort((left, right) => left.product.name.localeCompare(right.product.name, 'pl'));
  }, [filterAvailable, variants]);

  const categories = useMemo(() => {
    const grouped = new Map<string, { name: string; color?: string | null; products: CatalogProduct[] }>();
    products.forEach((product) => {
      const key = getCategoryKey(product.product);
      const existing = grouped.get(key);
      if (existing) {
        existing.products.push(product);
      } else {
        grouped.set(key, {
          name: product.product.productCategoryName || t('products:productCategory.none'),
          color: product.product.productCategoryColor,
          products: [product],
        });
      }
    });
    const categoryEntries = Array.from(grouped.entries()).sort(([, left], [, right]) => (
      left.name.localeCompare(right.name, 'pl')
    ));
    const preferredIds = new Set(preferredVariantIds);
    const preferredProducts = products.filter((product) => (
      product.selectableVariants.some((variant) => preferredIds.has(variant.id))
    ));
    return preferredProducts.length > 0
      ? [[PREFERRED_KEY, {
          name: t('products:catalogPicker.preferredCategory'),
          color: null,
          products: preferredProducts,
        }] as [string, { name: string; color?: string | null; products: CatalogProduct[] }], ...categoryEntries]
      : categoryEntries;
  }, [preferredVariantIds, products, t]);

  const selectedCategory = categories.find(([key]) => key === selectedCategoryKey);
  const selectedProduct = products.find((product) => product.id === selectedProductId);

  const searchResults = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('pl');
    if (!query) return [];
    return products.flatMap((product) => product.selectableVariants)
      .filter((variant) => buildProductSelectorSearchText(variant).includes(query));
  }, [products, search]);

  const chooseProduct = (product: CatalogProduct) => {
    if (product.realVariants.length === 0) {
      const fallback = product.defaultVariant;
      if (!fallback) {
        message.error(t('products:catalogPicker.noSelectableVariant'));
        return;
      }
      onSelect(fallback);
      return;
    }
    setSelectedProductId(product.id);
  };

  const renderVariantMeta = (variant: ProductVariant) => {
    const dimensions = getProductSelectorDimensions(variant);
    const ean = getProductSelectorEan(variant);
    const available = Number(variant.inventory?.quantityAvailable || 0);
    return (
      <span className="product-picker-modal__meta">
        {dimensions && <span>{dimensions}</span>}
        {ean && <span>EAN {ean}</span>}
        {showInventory && (
          <Tag color={available > 0 ? 'success' : 'default'}>
            {t('products:catalogPicker.availableStock', {
              quantity: available,
              unit: formatUnit(variant.product?.baseUnit),
            })}
          </Tag>
        )}
      </span>
    );
  };

  const renderSearchResults = () => (
    <div className="product-picker-modal__list" role="list">
      {searchResults.map((variant) => (
        <button
          key={variant.id}
          type="button"
          className={`product-picker-modal__row${variant.id === selectedVariantId ? ' is-selected' : ''}`}
          onClick={() => onSelect(variant)}
        >
          <span className="product-picker-modal__row-main">
            <strong>{getProductSelectionLabel(variant, t('products:catalogPicker.unnamedProduct'))}</strong>
            <span className="product-picker-modal__breadcrumb-text">
              {[
                variant.product?.productCategoryName || t('products:productCategory.none'),
                variant.product?.name,
                getProductVariantDisplayName(
                  variant,
                  t('products:catalogPicker.basicProduct'),
                ),
              ].filter(Boolean).join(' → ')}
            </span>
            {renderVariantMeta(variant)}
          </span>
          <RightOutlined aria-hidden="true" />
        </button>
      ))}
      {searchResults.length === 0 && !loading && <Empty description={t('products:catalogPicker.noResults')} />}
    </div>
  );

  const renderCategories = () => (
    <div className="product-picker-modal__grid" role="list">
      {categories.map(([key, category]) => (
        <button
          key={key}
          type="button"
          className="product-picker-modal__category"
          onClick={() => setSelectedCategoryKey(key)}
        >
          <span
            className="product-picker-modal__category-dot"
            style={{ backgroundColor: category.color || '#94a3b8' }}
            aria-hidden="true"
          />
          <span className="product-picker-modal__row-main">
            <strong>{category.name}</strong>
            <Text type="secondary">
              {t('products:catalogPicker.categoryProductCount', { count: category.products.length })}
            </Text>
          </span>
          <RightOutlined aria-hidden="true" />
        </button>
      ))}
    </div>
  );

  const renderProducts = () => (
    <div className="product-picker-modal__list" role="list">
      {(selectedCategory?.[1].products || []).map((product) => {
        const representative = product.selectableVariants[0];
        return (
          <button
            key={product.id}
            type="button"
            className="product-picker-modal__row"
            onClick={() => chooseProduct(product)}
          >
            <span className="product-picker-modal__row-main">
              <strong>{product.product.name}</strong>
              {renderVariantMeta(representative)}
              {product.realVariants.length > 0 && (
                <Text type="secondary">
                  {t('products:catalogPicker.variantCount', { count: product.realVariants.length })}
                </Text>
              )}
            </span>
            <RightOutlined aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );

  const renderVariants = () => (
    <div className="product-picker-modal__list" role="list">
      {(selectedProduct?.selectableVariants || []).map((variant) => {
        const attributeEntries = getProductVariantAttributeEntries(variant);
        return (
          <button
            key={variant.id}
            type="button"
            className={`product-picker-modal__row${variant.id === selectedVariantId ? ' is-selected' : ''}`}
            onClick={() => onSelect(variant)}
          >
            <span className="product-picker-modal__row-main">
              {!isRealProductVariant(variant) ? (
                <strong>
                  {getProductVariantDisplayName(
                    variant,
                    t('products:catalogPicker.basicProduct'),
                  )}
                </strong>
              ) : attributeEntries.length > 0 ? (
                <span className="product-picker-modal__attributes">
                  {attributeEntries.map(([key, value]) => (
                    <Tag key={key}>{key}: {value}</Tag>
                  ))}
                </span>
              ) : (
                <strong>{getProductVariantName(variant)}</strong>
              )}
              {renderVariantMeta(variant)}
            </span>
            <RightOutlined aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );

  const isSearching = Boolean(search.trim());

  return (
    <Modal
      title={t('products:catalogPicker.modalTitle')}
      open={open}
      onCancel={onCancel}
      footer={(
        <div className="product-picker-modal__footer">
          {canCreate && (
            <Button icon={<PlusOutlined />} onClick={onRequestCreate}>
              {t('products:actions.addNewProduct')}
            </Button>
          )}
          <Button onClick={onCancel}>{t('common:cancel')}</Button>
        </div>
      )}
      width={760}
      destroyOnHidden
    >
      <Input
        autoFocus
        allowClear
        prefix={<SearchOutlined aria-hidden="true" />}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t('products:catalogPicker.searchPlaceholder')}
        aria-label={t('products:catalogPicker.searchLabel')}
      />

      {!isSearching && (
        <div className="product-picker-modal__navigation">
          {(selectedCategoryKey || selectedProductId) && (
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => {
                if (selectedProductId) setSelectedProductId(undefined);
                else setSelectedCategoryKey(undefined);
              }}
            >
              {t('common:back')}
            </Button>
          )}
          <Breadcrumb
            items={[
              { title: t('products:catalogPicker.categoryStep') },
              ...(selectedCategory ? [{ title: selectedCategory[1].name }] : []),
              ...(selectedProduct ? [{ title: selectedProduct.product.name }] : []),
            ]}
          />
        </div>
      )}

      <Spin spinning={loading}>
        <div className="product-picker-modal__content">
          {isSearching
            ? renderSearchResults()
            : !selectedCategoryKey
              ? renderCategories()
              : !selectedProductId
                ? renderProducts()
                : renderVariants()}
        </div>
      </Spin>
    </Modal>
  );
};

export default ProductPickerModal;
